import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Printer } from "lucide-react";
import { adfAssets } from "../storefront/assets";
import { formatAZN, pickLocale } from "../../lib/format";
import { useI18n } from "../../lib/i18n";

/**
 * A branded, printable invoice for one order.
 *
 * The old "invoice" button called window.print() on the whole account page —
 * headers, tabs and all. This is a real document: the atelier's mark, the
 * order's number and time, every piece with its size and price, the delivery
 * method and address, the payment method, and the full money breakdown. Its
 * own print stylesheet hides everything else so a clean sheet comes out.
 */
const PROVIDER_LABELS = {
  cod: { az: "Çatdırılma zamanı nağd", en: "Cash on delivery" },
  bank_transfer: { az: "Bank köçürməsi", en: "Bank transfer" },
  kapitalbank: { az: "Kartla ödəniş (Kapital Bank)", en: "Card (Kapital Bank)" },
  stripe: { az: "Kart (Stripe)", en: "Card (Stripe)" },
};

export default function OrderInvoice({ order, tracking, open, onClose }) {
  const { t, locale } = useI18n();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !order) return null;

  const placed = order.placed_at ? new Date(order.placed_at) : null;
  const dateStr = placed
    ? placed.toLocaleString(locale === "az" ? "az-AZ" : "en-US", {
        day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
      })
    : "—";
  const addr = order.shipping_address || {};
  const addrLine = [addr.line1, addr.line2, addr.city, addr.country].filter(Boolean).join(", ");
  const providerLabel = pickLocale(PROVIDER_LABELS[order.payment_provider], locale) || order.payment_provider || "—";
  const deliveryName = order.delivery_method === "store"
    ? t("invoice.pickup", "Mağazadan götürmə")
    : (order.delivery_method || t("checkout.delivery.courier", "Çatdırılma"));

  const promo = (order.discount_total || 0) - (order.points_discount_minor || 0);
  const items = Array.isArray(order.items) ? order.items : [];

  return createPortal(
    <div className="adf-invoice-overlay" role="dialog" aria-modal="true" data-testid="order-invoice">
      <div className="adf-invoice-toolbar">
        <button type="button" onClick={() => window.print()} className="adf-invoice-btn adf-invoice-btn--dark" data-testid="invoice-print">
          <Printer size={16} aria-hidden="true" /> {t("invoice.print", "Çap et")}
        </button>
        <button type="button" onClick={onClose} className="adf-invoice-btn" aria-label={t("common.close", "Bağla")}>
          <X size={16} aria-hidden="true" /> {t("common.close", "Bağla")}
        </button>
      </div>

      <div className="adf-invoice-sheet" data-testid="invoice-sheet">
        <header className="adf-invoice-head">
          <img src={adfAssets.logoWord} alt="Atelier Di Falco" className="adf-invoice-logo" />
          <div className="adf-invoice-meta">
            <div className="adf-invoice-title">{t("invoice.title", "Hesab-faktura")}</div>
            <div className="adf-invoice-num">#{order.display_id}</div>
            <div className="adf-invoice-date">{dateStr}</div>
          </div>
        </header>

        <div className="adf-invoice-cols">
          <div>
            <div className="adf-invoice-label">{t("invoice.bill_to", "Sifariş")}</div>
            <div className="adf-invoice-value">
              {[addr.first_name, addr.last_name].filter(Boolean).join(" ") || "—"}<br />
              {addr.phone && <>{addr.phone}<br /></>}
              {order.email && <>{order.email}<br /></>}
            </div>
          </div>
          <div>
            <div className="adf-invoice-label">{t("invoice.delivery", "Çatdırılma")}</div>
            <div className="adf-invoice-value">
              {deliveryName}<br />
              {order.pickup_store
                ? <>{pickLocale(order.pickup_store.title, locale)}<br />{pickLocale(order.pickup_store.address, locale)}</>
                : (addrLine || "—")}
            </div>
          </div>
          <div>
            <div className="adf-invoice-label">{t("invoice.payment", "Ödəniş")}</div>
            <div className="adf-invoice-value">
              {providerLabel}<br />
              <span className="adf-invoice-status">{t(`status.payment.${order.payment_status}`, order.payment_status)}</span>
            </div>
          </div>
        </div>

        <table className="adf-invoice-table">
          <thead>
            <tr>
              <th>{t("invoice.item", "Məhsul")}</th>
              <th>{t("invoice.variant", "Ölçü / rəng")}</th>
              <th className="ar">{t("invoice.qty", "Say")}</th>
              <th className="ar">{t("invoice.unit", "Qiymət")}</th>
              <th className="ar">{t("invoice.sum", "Cəmi")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id}>
                <td>
                  <div className="adf-invoice-item-name">{pickLocale(it.title_snapshot, locale) || it.sku}</div>
                  <div className="adf-invoice-item-sku">SKU {it.sku}</div>
                </td>
                <td>{it.variant_title || "—"}</td>
                <td className="ar">{it.quantity}</td>
                <td className="ar">{formatAZN(it.unit_price, locale)}</td>
                <td className="ar">{formatAZN(it.total, locale)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="adf-invoice-totals">
          <Row label={t("invoice.subtotal", "Ara cəm")} value={formatAZN(order.subtotal, locale)} />
          {promo > 0 && <Row label={t("invoice.discount", "Endirim")} value={`− ${formatAZN(promo, locale)}`} />}
          {(order.points_discount_minor || 0) > 0 && (
            <Row label={`${t("invoice.points", "Falcon xalları")}${order.points_spent ? ` (${order.points_spent})` : ""}`} value={`− ${formatAZN(order.points_discount_minor, locale)}`} />
          )}
          {(order.gift_card_total || 0) > 0 && <Row label={t("invoice.gift_card", "Hədiyyə kartı")} value={`− ${formatAZN(order.gift_card_total, locale)}`} />}
          <Row label={t("invoice.shipping", "Çatdırılma")} value={(order.shipping_total || 0) > 0 ? formatAZN(order.shipping_total, locale) : t("checkout.delivery.free", "Ödənişsiz")} />
          <Row label={t("invoice.total", "Yekun")} value={formatAZN(order.total, locale)} strong />
        </div>

        {tracking?.timeline?.length > 0 && (
          <div className="adf-invoice-timeline">
            <div className="adf-invoice-label">{t("invoice.timeline", "Sifariş tarixçəsi")}</div>
            {tracking.timeline.map((e) => (
              <div key={e.id} className="adf-invoice-tl-row">
                <span>{t(`status.order.${e.to}`, e.to)}</span>
                <span>{e.at ? new Date(e.at).toLocaleString(locale === "az" ? "az-AZ" : "en-US", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</span>
              </div>
            ))}
          </div>
        )}

        <footer className="adf-invoice-foot">
          <div>Atelier Di Falco</div>
          <div>{t("invoice.footer", "Bizi seçdiyiniz üçün təşəkkür edirik.")}</div>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

function Row({ label, value, strong }) {
  return (
    <div className={`adf-invoice-total-row ${strong ? "is-strong" : ""}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
